import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronRight,
  Users,
  Plus,
  Eye,
  EyeOff,
  ToggleLeft,
  ToggleRight,
  KeyRound,
  X,
  Save,
  Shield,
  UserCog,
} from "lucide-react";
import { useAuth } from "../utils/auth";
import { isAdmin, getRoleLabel, ROLES } from "../utils/roles";
import {
  fetchAdminUsers,
  createAdminUser,
  updateAdminUser,
  deactivateAdminUser,
} from "../api/AdminUsersAPIs";

const ROLE_OPTIONS = [
  { value: ROLES.ADMIN, label: "Admin — صلاحيات كاملة" },
  { value: ROLES.MODERATOR, label: "Moderator — تشغيل الأوردرات" },
  { value: ROLES.VIEWER, label: "Viewer — عرض فقط" },
];

const emptyForm = {
  email: "",
  password: "",
  role: ROLES.VIEWER,
};

const roleBadgeClass = (role) => {
  switch (role) {
    case ROLES.ADMIN:
      return "bg-purple-100 text-purple-800 border-purple-200";
    case ROLES.MODERATOR:
      return "bg-blue-100 text-blue-800 border-blue-200";
    case ROLES.VIEWER:
      return "bg-slate-100 text-slate-700 border-slate-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const UsersManagementPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authState } = useAuth();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetPassword, setResetPassword] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isAdmin(authState.role)) {
      navigate("/login");
    }
  }, [authState.role, navigate]);

  const { data: users = [], isLoading, error, refetch } = useQuery({
    queryKey: ["adminUsers"],
    queryFn: fetchAdminUsers,
    enabled: isAdmin(authState.role),
  });

  const openCreate = () => {
    setForm(emptyForm);
    setShowPassword(false);
    setShowCreate(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createAdminUser(form);
      toast.success("تم إنشاء الحساب بنجاح");
      setShowCreate(false);
      setForm(emptyForm);
      queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
    } catch (err) {
      toast.error(err.message || "فشل إنشاء الحساب");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    const next = !user.isActive;
    const label = next ? "تفعيل" : "تعطيل";
    if (!window.confirm(`${label} حساب ${user.email}؟`)) return;
    try {
      if (next) {
        await updateAdminUser(user.id, { isActive: true });
      } else {
        await deactivateAdminUser(user.id);
      }
      toast.success(next ? "تم تفعيل الحساب" : "تم تعطيل الحساب");
      refetch();
    } catch (err) {
      toast.error(err.message || "فشلت العملية");
    }
  };

  const handleRoleChange = async (user, role) => {
    if (role === user.role) return;
    if (!window.confirm(`تغيير دور ${user.email} إلى ${getRoleLabel(role)}؟`)) return;
    try {
      await updateAdminUser(user.id, { role });
      toast.success("تم تحديث الدور");
      refetch();
    } catch (err) {
      toast.error(err.message || "فشل تحديث الدور");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetUserId) return;
    setSaving(true);
    try {
      await updateAdminUser(resetUserId, { password: resetPassword });
      toast.success("تم تغيير كلمة المرور");
      setResetUserId(null);
      setResetPassword("");
    } catch (err) {
      toast.error(err.message || "فشل تغيير كلمة المرور");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" rtl />
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4 bg-white/80 backdrop-blur-md shadow-xl rounded-2xl p-4 md:p-6 border border-white/30">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="p-2 rounded-xl hover:bg-indigo-50 text-indigo-600 transition"
              title="رجوع"
            >
              <ChevronRight size={24} />
            </button>
            <div className="flex items-center gap-2">
              <Users className="text-indigo-600" size={28} />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">إدارة الحسابات</h1>
                <p className="text-sm text-gray-500">إنشاء Admin / Moderator / Viewer</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-[1.02] transition"
          >
            <Plus size={20} />
            حساب جديد
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
            {error.message}
          </div>
        )}

        <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl border border-white/40 overflow-hidden">
          {isLoading ? (
            <div className="p-10 text-center text-gray-500 animate-pulse">جاري التحميل...</div>
          ) : users.length === 0 ? (
            <div className="p-10 text-center text-gray-500">لا توجد حسابات بعد</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-right">
                <thead>
                  <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
                    <th className="p-4 font-bold text-indigo-900">البريد</th>
                    <th className="p-4 font-bold text-indigo-900">الدور</th>
                    <th className="p-4 font-bold text-indigo-900">الحالة</th>
                    <th className="p-4 font-bold text-indigo-900">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-gray-100 hover:bg-indigo-50/40">
                      <td className="p-4 font-medium text-gray-800" style={{ fontFamily: "monospace" }}>
                        {user.email}
                      </td>
                      <td className="p-4">
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user, e.target.value)}
                          className={`rounded-lg border px-3 py-2 text-sm font-bold ${roleBadgeClass(user.role)}`}
                        >
                          {ROLE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {getRoleLabel(opt.value)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="p-4">
                        <span
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                            user.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {user.isActive ? "نشط" : "معطّل"}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleToggleActive(user)}
                            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 hover:bg-white text-sm font-medium transition"
                            title={user.isActive ? "تعطيل" : "تفعيل"}
                          >
                            {user.isActive ? (
                              <>
                                <ToggleRight size={16} className="text-green-600" /> تعطيل
                              </>
                            ) : (
                              <>
                                <ToggleLeft size={16} className="text-gray-500" /> تفعيل
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setResetUserId(user.id);
                              setResetPassword("");
                            }}
                            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 hover:bg-white text-sm font-medium transition"
                          >
                            <KeyRound size={16} className="text-amber-600" />
                            كلمة مرور
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
          <div className="bg-white/70 rounded-xl p-4 border border-purple-100 flex gap-3 items-start">
            <Shield className="text-purple-600 shrink-0" size={20} />
            <div>
              <p className="font-bold text-gray-800">Admin</p>
              <p>كل الصلاحيات + إدارة الحسابات</p>
            </div>
          </div>
          <div className="bg-white/70 rounded-xl p-4 border border-blue-100 flex gap-3 items-start">
            <UserCog className="text-blue-600 shrink-0" size={20} />
            <div>
              <p className="font-bold text-gray-800">Moderator</p>
              <p>تشغيل الأوردرات بدون أدوات الإدارة</p>
            </div>
          </div>
          <div className="bg-white/70 rounded-xl p-4 border border-slate-200 flex gap-3 items-start">
            <Eye className="text-slate-600 shrink-0" size={20} />
            <div>
              <p className="font-bold text-gray-800">Viewer</p>
              <p>عرض الأوردرات والتحليلات فقط</p>
            </div>
          </div>
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5" dir="rtl">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">إنشاء حساب جديد</h2>
              <button type="button" onClick={() => setShowCreate(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X size={22} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">البريد الإلكتروني</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-400 outline-none"
                  placeholder="user@example.com"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">كلمة المرور</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={8}
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 pl-12 focus:ring-2 focus:ring-indigo-400 outline-none"
                    style={{ fontFamily: "cairo black" }}
                    placeholder="8 أحرف على الأقل"
                    dir="ltr"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الدور</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-400 outline-none"
                >
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-bold disabled:opacity-50"
              >
                <Save size={18} />
                {saving ? "جاري الحفظ..." : "إنشاء الحساب"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetUserId != null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5" dir="rtl">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">تغيير كلمة المرور</h2>
              <button
                type="button"
                onClick={() => setResetUserId(null)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X size={22} />
              </button>
            </div>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <input
                type="password"
                required
                minLength={8}
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-400 outline-none"
                placeholder="كلمة المرور الجديدة (8+)"
                dir="ltr"
              />
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white py-3 rounded-xl font-bold disabled:opacity-50"
              >
                {saving ? "جاري الحفظ..." : "حفظ كلمة المرور"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagementPage;
